import React, { useState, useEffect, useMemo } from 'react';
import TimingLayout from '../../components/TimingLayout';
import { Row, Col, Button, Form, Modal } from 'react-bootstrap';
import { MdDateRange, MdInfoOutline } from 'react-icons/md';
import { FaClock, FaSignInAlt, FaSignOutAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import axios from 'axios';
import moment from 'moment';
import { getPayrollPeriod } from '../../utils/payrollPeriod';
import './time-history.css';

const TimeHistory = () => {
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(getPayrollPeriod(moment().toDate()));

  const [showBreakModal, setShowBreakModal] = useState(false);
  const [selectedDateForBreak, setSelectedDateForBreak] = useState(null);
  const [breakDetails, setBreakDetails] = useState(null);
  const [loadingBreaks, setLoadingBreaks] = useState(false);

  const currentPeriodDays = useMemo(() => {
    const days = [];
    let current = moment(selectedPeriod.startDate);
    const end = moment(selectedPeriod.endDate);
    while (current.isSameOrBefore(end, 'day')) {
      days.push(current.clone());
      current.add(1, 'day');
    }
    return days;
  }, [selectedPeriod]);

  const formatTime = (datetime) => (!datetime ? '—' : moment(datetime).format('hh:mm A'));
  const formatDate = (date) => moment(date).format('ddd DD MMM');

  const handlePreviousPeriod = () => {
    setSelectedPeriod(getPayrollPeriod(moment(selectedPeriod.startDate).subtract(1, 'day').toDate()));
  };
  const handleNextPeriod = () => {
    setSelectedPeriod(getPayrollPeriod(moment(selectedPeriod.endDate).add(1, 'day').toDate()));
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    const userId = localStorage.getItem('userId');
    const startDateStr = moment(selectedPeriod.startDate).format('YYYY-MM-DD');
    const endDateStr = moment(selectedPeriod.endDate).format('YYYY-MM-DD');

    if (!userId) {
      setLoading(false);
      setError('User is not logged in or userId is missing.');
      return;
    }

    axios
      .get(`https://backend.ems.uk.hiradentallab.com/api/attendance/attendance/range/${userId}`, {
        params: { startDate: startDateStr, endDate: endDateStr },
      })
      .then((response) => {
        if (response.data.success) setData(response.data.data || []);
        else setError(response.data.message || 'Failed to fetch attendance data.');
        setLoading(false);
      })
      .catch((error) => {
        const errorMsg = error.response?.data?.message || error.message || 'An error occurred.';
        setError(`Network Error: ${errorMsg}`);
        setLoading(false);
      });
  }, [selectedPeriod]);

  const filteredData = data.filter((item) => {
    if (!searchQuery) return true;
    return moment(item.date).format('YYYY-MM-DD').toLowerCase().includes(searchQuery);
  });

  const getAttendanceForDay = (day) => {
    const dayStr = day.format('YYYY-MM-DD');
    return filteredData.find((record) => moment(record.date).format('YYYY-MM-DD') === dayStr);
  };

  const handleShowBreakDetails = async (record) => {
    if (!record) return;
    setSelectedDateForBreak(record.date);
    setShowBreakModal(true);
    setLoadingBreaks(true);
    setBreakDetails(null);
    try {
      const userId = localStorage.getItem('userId');
      const dateStr = moment(record.date).format('YYYY-MM-DD');
      const response = await axios.get('https://backend.ems.uk.hiradentallab.com/api/attendance/breaks', {
        params: { userId, date: dateStr }
      });
      if (response.data.success) setBreakDetails(response.data.data);
    } catch (err) {
      console.error('Error fetching break details:', err);
    } finally {
      setLoadingBreaks(false);
    }
  };

  const totalWorkedHours = data.reduce((sum, item) => sum + (parseFloat(item.workedHours) || 0), 0).toFixed(2);

  return (
    <div className='container-fluid time-history-page'>
      <TimingLayout>

        <h2 className="current-month-title">
          <MdDateRange style={{ marginRight: '8px' }} />
          Employee Timesheet
        </h2>
        <p className="period-subtitle">{selectedPeriod.label}</p>

        <div className="timesheet-container">

          {/* Toolbar */}
          <div className="toolbar">
            <div className="period-nav">
              <button
                type="button"
                className="nav-btn"
                onClick={handlePreviousPeriod}
                aria-label="Previous period"
              >
                <FaChevronLeft size={13} />
              </button>
              <span className="period-label">{selectedPeriod.label}</span>
              <button
                type="button"
                className="nav-btn"
                onClick={handleNextPeriod}
                aria-label="Next period"
              >
                <FaChevronRight size={13} />
              </button>
            </div>
            <div className="toolbar-right">
              <button className="link-btn">Export Excel</button>
              <button className="link-btn">Export PDF</button>
              <Form.Control
                type="search"
                placeholder="Search date..."
                className="search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
              />
            </div>
          </div>

          {/* Table */}
          <div className="table-wrapper">
            <table className="timesheet-table">
              <thead>
                <tr>
                  <th style={{ width: '18%' }}>Date</th>
                  <th style={{ width: '14%' }}>In</th>
                  <th style={{ width: '14%' }}>Out</th>
                  <th style={{ width: '14%' }}>Hours</th>
                  <th style={{ width: '12%' }}>Break</th>
                  <th style={{ width: '28%' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="center-cell">Loading...</td></tr>
                ) : error ? (
                  <tr><td colSpan="6" className="center-cell error-text">{error}</td></tr>
                ) : (
                  currentPeriodDays.map((day, index) => {
                    const record = getAttendanceForDay(day);
                    const isToday = day.isSame(moment(), 'day');
                    const isFuture = day.isAfter(moment(), 'day');

                    if (record) {
                      const status = record.status || 'present';
                      const isPresent = status === 'present';
                      const isHoliday = status === 'holiday';
                      const isLeave = status === 'leave';
                      const isAbsent = status === 'absent';
                      const isWeekoff = status === 'weekoff';

                      let statusText = 'Present';
                      let statusClass = 'badge-present';
                      if (isHoliday) { statusText = 'Holiday'; statusClass = 'badge-holiday'; }
                      else if (isLeave) { statusText = 'Leave'; statusClass = 'badge-leave'; }
                      else if (isAbsent) { statusText = 'Absent'; statusClass = 'badge-absent'; }
                      else if (isWeekoff) { statusText = 'Week Off'; statusClass = 'badge-weekoff'; }

                      return (
                        <tr key={index} className={isToday ? 'row-today' : ''}>
                          <td className="date-col" data-label="Date">
                            {formatDate(day)}
                            {isToday && <span className="today-tag">Today</span>}
                          </td>
                          <td data-label="In" className={!isPresent ? 'is-empty' : ''}>
                            {isPresent ? (
                              <span className="time-text in-text">
                                <FaSignInAlt size={10} /> {formatTime(record.checkIn)}
                              </span>
                            ) : '—'}
                          </td>
                          <td data-label="Out" className={!isPresent ? 'is-empty' : ''}>
                            {isPresent ? (
                              <span className="time-text out-text">
                                <FaSignOutAlt size={10} /> {formatTime(record.checkOut)}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="hours-col" data-label="Hours">
                            {record.workedHours} hrs
                            {(isHoliday || isLeave || isAbsent || isWeekoff) && (
                              <span className="mini-tag">{isHoliday ? 'HOL' : isLeave ? 'LVE' : isAbsent ? 'ABS' : 'WO'}</span>
                            )}
                          </td>
                          <td
                            data-label="Break"
                            className={!(isPresent && record.totalBreakMinutes > 0) ? 'is-empty' : ''}
                          >
                            {isPresent && record.totalBreakMinutes > 0
                              ? `${record.totalBreakMinutes} min`
                              : '—'}
                          </td>
                          <td data-label="Status" className="status-cell">
                            <div className="status-row">
                              <span className={`badge ${statusClass}`}>{statusText}</span>
                              {(isHoliday || isLeave) && record.leaveType && (
                                <span className="badge badge-outline">{record.leaveType}</span>
                              )}
                              {isPresent && (
                                <button className="break-btn" onClick={() => handleShowBreakDetails(record)}>
                                  <FaClock size={10} /> Breaks
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={index} className={isFuture ? 'row-future' : ''}>
                        <td className="date-col muted" data-label="Date">{formatDate(day)}</td>
                        <td data-label="In" className="is-empty">—</td>
                        <td data-label="Out" className="is-empty">—</td>
                        <td data-label="Hours" className="is-empty">—</td>
                        <td data-label="Break" className="is-empty">—</td>
                        <td data-label="Status" className="status-cell">
                          {isFuture
                            ? <span className="upcoming">Upcoming</span>
                            : '—'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {!loading && !error && (
                <tfoot>
                  <tr>
                    <td colSpan={3} className="total-label">Total Worked Hours</td>
                    <td colSpan={3} className="total-value">{totalWorkedHours} hrs</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </TimingLayout>

      {/* Break Details Modal */}
      <Modal show={showBreakModal} onHide={() => setShowBreakModal(false)} size="lg" centered className="simple-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaClock className="me-2" /> Break Details — {moment(selectedDateForBreak).format('DD MMM YYYY')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingBreaks ? (
            <div className="center-cell">Loading...</div>
          ) : breakDetails ? (
            <>
              {/* Summary */}
              <div className="summary-grid">
                <div className="summary-box">
                  <span>Total Break</span>
                  <strong className="blue">{breakDetails.summary?.totalBreakMinutes || 0} min</strong>
                </div>
                <div className="summary-box">
                  <span>Paid</span>
                  <strong className="green">{breakDetails.summary?.totalPaid || 0} min</strong>
                </div>
                <div className="summary-box">
                  <span>Unpaid</span>
                  <strong className="red">{breakDetails.summary?.totalUnpaid || 0} min</strong>
                </div>
              </div>

              {breakDetails.isAutoDeducted && breakDetails.autoBreakInfo && (
                <div className="info-box">
                  <MdInfoOutline size={18} />
                  <span>
                    <strong>Auto-deducted:</strong> {breakDetails.autoBreakInfo.message} ({breakDetails.autoBreakInfo.deductedFromHours} min)
                  </span>
                </div>
              )}

              {/* Logs */}
              <h6 className="section-heading">Break Logs</h6>
              {breakDetails.breaks && breakDetails.breaks.length > 0 ? (
                <table className="logs-table">
                  <thead>
                    <tr>
                      <th>Break In</th>
                      <th>Break Out</th>
                      <th>Duration</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakDetails.breaks.map((log, idx) => (
                      <tr key={idx}>
                        <td>{moment(log.breakIn).format('hh:mm A')}</td>
                        <td>{moment(log.breakOut).format('hh:mm A')}</td>
                        <td>{log.durationMinutes} min</td>
                        <td>
                          <span className={`badge ${log.breakType === 'paid' ? 'badge-present' : log.breakType === 'unpaid' ? 'badge-absent' : 'badge-holiday'}`}>
                            {log.breakType}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="empty-box">No manual break logs recorded.</div>
              )}

              {/* Time Summary */}
              {breakDetails.attendance && (
                <>
                  <h6 className="section-heading">Time Summary</h6>
                  <div className="time-summary-row">
                    <span>Worked Hours</span>
                    <strong className="blue">{breakDetails.attendance.workedHours} hrs</strong>
                  </div>
                  <div className="time-summary-row">
                    <span>Break Deduction</span>
                    <strong className="red">{breakDetails.attendance.breakDeductionMinutes} min</strong>
                  </div>
                  <div className="time-summary-row">
                    <span>Paid Break</span>
                    <strong className="green">{breakDetails.attendance.totalPaidBreakMinutes} min</strong>
                  </div>
                  <div className="time-summary-row">
                    <span>Unpaid Break</span>
                    <strong className="orange">{breakDetails.attendance.totalUnpaidBreakMinutes} min</strong>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="empty-box">No break details available.</div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default TimeHistory;